import { Link } from "react-router-dom";
import { MapPin, ArrowRight, Palmtree } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { SERVICE_CITIES, SURROUNDING_AREAS, HAWAII_ISLANDS, BUSINESS } from "@/lib/constants";
import { serviceAreaSchema } from "@/lib/structuredData";

export default function ServiceAreas() {
  return (
    <div data-testid="page-areas">
      <SEO
        title="Service Areas — Carfornia Buyers | Bay Area, Northern California & Oahu"
        description="Carfornia Buyers is based in Hayward and buys cars across the San Francisco Bay Area and surrounding areas — including Oakland, Fremont, San Jose, Sacramento and Stockton — and on Oahu, Hawaii."
        canonical="https://carforniabuyers.com/service-areas"
        jsonLd={serviceAreaSchema()}
      />
      <section className="py-16 lg:py-24 bg-brand-surface">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-blue">Service areas</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight mt-3">
            Serving the Bay Area, surrounding areas — and Oahu.
          </h1>
          <p className="text-lg text-slate-600 mt-5 max-w-2xl mx-auto">
            We are based in Hayward and buy cars throughout the San Francisco Bay Area and the surrounding region. For most sellers we come to you for the inspection. We also buy cars on Oahu, Hawaii. A little farther out? Submit your request and we will confirm whether we can help.
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICE_CITIES.map((c) => (
              <div
                key={c.name}
                data-testid={c.testId}
                className="group bg-white border border-slate-100 rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-blue-50 text-brand-blue"><MapPin className="w-5 h-5" /></span>
                  <span className="text-xs font-semibold uppercase tracking-wider text-brand-orange">Bay Area</span>
                </div>
                <h3 className="font-display text-xl font-bold text-slate-900 group-hover:text-brand-blue transition-colors">{c.name}</h3>
                <p className="text-slate-600 mt-2 text-[15px] leading-relaxed">{c.desc}</p>
                <Link to={c.slug ? `/sell-my-car/${c.slug}` : "/sell-your-car"} data-testid={c.slug ? `city-link-${c.slug}` : undefined} className="inline-flex items-center gap-2 mt-4 font-semibold text-brand-blue group-hover:gap-3 transition-all text-sm">
                  {c.slug ? `Selling a car in ${c.name}? Read more` : `Request an offer in ${c.name}`} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
          <div className="mt-10 rounded-3xl border border-emerald-100 bg-emerald-50/50 p-7 sm:p-9 grid grid-cols-1 md:grid-cols-12 gap-6 items-center" data-testid="hawaii-section">
            <div className="md:col-span-8">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-emerald-700"><Palmtree className="w-4 h-4" /> Also serving Oahu, Hawaii</div>
              <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-2">We buy cars on Oahu, too.</h2>
              <p className="text-slate-600 mt-3 leading-relaxed">
                Selling a car anywhere on Oahu — Honolulu, Pearl City, Kapolei, Kaneohe and beyond? Submit your request with your Oahu ZIP code. We will contact you to walk through how inspection, title paperwork, payment and vehicle pickup work for island vehicles — and, as always, your real offer comes in writing after we have verified the car. Other islands are not served at this time.
              </p>
              <div className="flex flex-wrap gap-2 mt-4">
                {HAWAII_ISLANDS.map((a) => (
                  <span key={a} className="px-3 py-1.5 rounded-full bg-white border border-emerald-200 text-sm font-semibold text-slate-700">{a}</span>
                ))}
              </div>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Link to="/sell-your-car">
                <Button data-testid="hawaii-cta" className="h-12 px-6 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl">Request an Oahu offer <ArrowRight className="w-4 h-4 ml-2" /></Button>
              </Link>
            </div>
          </div>

          <div className="mt-6 rounded-2xl border border-slate-100 bg-brand-surface p-6" data-testid="surrounding-areas">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-blue">Surrounding areas we also cover</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {SURROUNDING_AREAS.map((a) => (
                <span key={a} className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-sm font-semibold text-slate-700">{a}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-brand-surface">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            Don’t see your city?
          </h2>
          <p className="text-slate-600 mt-4 leading-relaxed">
            Our home base is {BUSINESS.address}. We cover the Bay Area and surrounding areas — including Sacramento, Stockton, Modesto, Santa Rosa, Santa Cruz and Monterey. Submit your car or call <a href={BUSINESS.phoneHref} className="text-brand-blue font-semibold">{BUSINESS.phone}</a> and we will confirm whether we can come to you.
          </p>
          <Link to="/sell-your-car">
            <Button data-testid="areas-cta" className="mt-7 h-13 px-8 bg-[#FF6B00] hover:bg-[#E66000] text-white font-bold rounded-xl">Request my offer <ArrowRight className="w-4 h-4 ml-2" /></Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
